/**
 * Modal component — drives the `.oc-modal` native <dialog> elements.
 *
 * Focus trapping, Esc-to-close and the inert background come from
 * showModal() itself; this only wires the triggers and the ways to close.
 *
 * A trigger is any element with `aria-haspopup="dialog"` and an
 * `aria-controls` pointing at the dialog's id. Before opening, the dialog
 * receives an `oc-modal:open` event carrying the trigger, so consumers can
 * fill the dialog in based on what was clicked without re-implementing any of
 * the open/close behaviour. See /doc/components/modal.md.
 */
(function () {
	const OPEN_EVENT = "oc-modal:open";

	/**
	 * Opens a dialog, announcing which trigger caused it.
	 *
	 * @param {HTMLDialogElement} modal   The dialog to open.
	 * @param {HTMLElement|null}  trigger The element that requested it.
	 */
	const open = (modal, trigger = null) => {
		if (typeof modal.showModal !== "function") {
			return;
		}

		// Dispatched before showing so listeners can fill it in off-screen.
		modal.dispatchEvent(
			new CustomEvent(OPEN_EVENT, { detail: { trigger }, bubbles: true })
		);
		modal.showModal();
	};

	/**
	 * Wires the ways to close one dialog.
	 *
	 * @param {HTMLDialogElement} modal The dialog to wire.
	 */
	const setUpModal = (modal) => {
		if (modal.dataset.ocModalReady) {
			return;
		}
		modal.dataset.ocModalReady = "true";

		modal.querySelectorAll("[data-modal-close]").forEach((button) => {
			button.addEventListener("click", () => modal.close());
		});

		// A click landing on the dialog itself is a click on its backdrop.
		modal.addEventListener("click", (event) => {
			if (event.target === modal) {
				modal.close();
			}
		});
	};

	/**
	 * Wires every dialog and trigger currently in the document. Safe to call
	 * again after injecting markup: already-wired elements are skipped.
	 */
	const init = () => {
		document.querySelectorAll(".oc-modal").forEach(setUpModal);

		document
			.querySelectorAll('[aria-haspopup="dialog"][aria-controls]')
			.forEach((trigger) => {
				if (trigger.dataset.ocModalBound) {
					return;
				}
				trigger.dataset.ocModalBound = "true";

				trigger.addEventListener("click", () => {
					const modal = document.getElementById(
						trigger.getAttribute("aria-controls")
					);
					if (modal) {
						open(modal, trigger);
					}
				});
			});
	};

	document.addEventListener("DOMContentLoaded", init);

	// Exposed so blocks can open a dialog themselves or re-scan the document.
	window.ocModal = { open, init, OPEN_EVENT };
})();
